import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Building2, Download, Home, KeyRound, Loader2, Lock, LogOut, RefreshCw, Search, Trash2, X,
  Wallet as WalletIcon, BarChart3, LifeBuoy, Send,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogoMark } from "@/components/LogoMark";
import { StatsTab } from "@/components/StatsTab";
import { TIPI_RICERCA, normTipo, cercoLabel } from "@/data/formOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("cm_token") || ""}` });

const formatDate = (iso) =>
  new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

const STATI = [
  { value: "nuova", label: "Nuova", cls: "bg-emerald-100 text-emerald-700" },
  { value: "in_contatto", label: "In contatto", cls: "bg-amber-100 text-amber-700" },
  { value: "in_trattativa", label: "In trattativa", cls: "bg-blue-100 text-blue-700" },
  { value: "chiusa", label: "Chiusa", cls: "bg-slate-200 text-slate-600" },
];

const statoInfo = (s) => STATI.find((x) => x.value === s) || STATI[0];

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      localStorage.setItem("cm_token", data.token);
      onLogin(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-5">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-[0_20px_60px_rgba(10,25,47,0.08)] p-8"
      >
        <div className="flex flex-col items-center mb-8">
          <LogoMark size={52} />
          <h1 className="font-display text-xl font-extrabold text-[#0A192F] mt-4">Area riservata</h1>
          <p className="text-xs text-slate-500 mt-1">La casa cerca te! · powered by Casamatch.org</p>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-sm font-semibold text-[#0A192F]">Email</Label>
            <Input id="admin-email" data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-sm font-semibold text-[#0A192F]">Password</Label>
            <Input id="admin-password" data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 rounded-xl" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A192F] text-white font-bold py-3.5 hover:bg-[#10B981] transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Lock className="w-4 h-4" />}
            Accedi
          </button>
        </div>
      </motion.form>
    </div>
  );
};

const ChangePasswordModal = ({ onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("La nuova password e la conferma non coincidono");
      return;
    }
    if (next.length < 8) {
      toast.error("La nuova password deve avere almeno 8 caratteri");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success("Password aggiornata con successo");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il cambio password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A192F]/50 backdrop-blur-sm px-5" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="change-password-modal"
        className="w-full max-w-sm bg-white rounded-3xl border border-slate-200 shadow-2xl p-8"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <KeyRound className="w-5 h-5 text-[#10B981]" />
            <h2 className="font-display text-lg font-extrabold text-[#0A192F]">Cambia password</h2>
          </div>
          <button type="button" onClick={onClose} data-testid="change-password-close" className="text-slate-400 hover:text-[#0A192F] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cp-current" className="text-sm font-semibold text-[#0A192F]">Password attuale</Label>
            <Input id="cp-current" data-testid="change-password-current" type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className="h-12 rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-next" className="text-sm font-semibold text-[#0A192F]">Nuova password (min. 8 caratteri)</Label>
            <Input id="cp-next" data-testid="change-password-new" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="h-12 rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cp-confirm" className="text-sm font-semibold text-[#0A192F]">Conferma nuova password</Label>
            <Input id="cp-confirm" data-testid="change-password-confirm" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 rounded-xl" />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="change-password-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] text-white font-bold py-3.5 hover:bg-[#059669] transition-colors duration-300 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4.5 h-4.5 animate-spin" /> : <Lock className="w-4 h-4" />}
            Salva nuova password
          </button>
        </div>
      </form>
    </div>
  );
};

const downloadCsv = async (path, filename) => {
  try {
    const { data } = await axios.get(`${API}${path}`, {
      headers: authHeaders(),
      withCredentials: true,
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("File CSV scaricato");
  } catch {
    toast.error("Errore durante il download");
  }
};

const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [leads, setLeads] = useState([]);
  const [activity, setActivity] = useState({ sblocchi: [], abbonamenti: [] });
  const [supportChats, setSupportChats] = useState([]);
  const [replyDraft, setReplyDraft] = useState({});
  const [sendingReply, setSendingReply] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [search, setSearch] = useState("");
  const [filterProvincia, setFilterProvincia] = useState("tutte");
  const [filterTipo, setFilterTipo] = useState("tutti");
  const [filterStato, setFilterStato] = useState("tutti");
  const [filterDal, setFilterDal] = useState("");
  const [filterAl, setFilterAl] = useState("");
  const [editingNote, setEditingNote] = useState({});
  const [savingNote, setSavingNote] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, l, a, s] = await Promise.all([
        axios.get(`${API}/admin/requests`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/agency-leads`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/agency-activity`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/support-chats`, { headers: authHeaders(), withCredentials: true }),
      ]);
      setRequests(r.data.requests);
      setLeads(l.data.leads);
      setActivity(a.data);
      setSupportChats(s.data.chats || []);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async (id) => {
    setSendingReply(id);
    try {
      await axios.post(
        `${API}/admin/support-chats/${id}/reply`,
        { risposta: replyDraft[id] || "" },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success("Risposta inviata via email");
      setReplyDraft((d) => {
        const c = { ...d };
        delete c[id];
        return c;
      });
      load();
    } catch {
      toast.error("Errore invio risposta");
    } finally {
      setSendingReply(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provinceDisponibili = useMemo(
    () => [...new Set(requests.map((r) => r.provincia))].sort(),
    [requests]
  );

  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      if (filterProvincia !== "tutte" && r.provincia !== filterProvincia) return false;
      if (filterTipo !== "tutti" && normTipo(r.tipo_ricerca) !== filterTipo) return false;
      if (filterStato !== "tutti" && (r.status || "nuova") !== filterStato) return false;
      if (filterDal && r.created_at < filterDal) return false;
      if (filterAl && r.created_at > filterAl + "T23:59:59") return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = [r.nome, r.comune, r.provincia, r.whatsapp, r.email, r.note, r.note_interne]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [requests, search, filterProvincia, filterTipo, filterStato, filterDal, filterAl]);

  const updateStatus = async (id, status) => {
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    try {
      await axios.patch(`${API}/admin/requests/${id}`, { status }, { headers: authHeaders(), withCredentials: true });
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Errore aggiornamento stato");
      load();
    }
  };

  const saveNote = async (id) => {
    setSavingNote(id);
    try {
      await axios.patch(
        `${API}/admin/requests/${id}`,
        { note_interne: editingNote[id] || "" },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success("Nota salvata");
      setEditingNote((n) => {
        const c = { ...n };
        delete c[id];
        return c;
      });
      load();
    } catch {
      toast.error("Errore salvataggio nota");
    } finally {
      setSavingNote(null);
    }
  };

  const deleteRequest = async (id) => {
    if (!window.confirm("Eliminare questa richiesta? L'operazione la rimuove dalla vista (i dati restano nel database per sicurezza).")) return;
    try {
      await axios.delete(`${API}/admin/requests/${id}`, { headers: authHeaders(), withCredentials: true });
      toast.success("Richiesta eliminata");
      setRequests((rs) => rs.filter((r) => r.id !== id));
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const rows = tab === "requests" ? filteredRequests : leads;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-[#0A192F] text-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-18 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <LogoMark size={38} />
            <div className="leading-tight">
              <p className="font-display font-extrabold">La casa cerca te!</p>
              <p className="text-[11px] text-slate-400">Pannello riservato · {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-semibold transition-colors">
              <RefreshCw className="w-3.5 h-3.5" /> Aggiorna
            </button>
            <button onClick={() => setShowPasswordModal(true)} data-testid="admin-change-password-button" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 px-4 py-2 text-sm font-semibold transition-colors">
              <KeyRound className="w-3.5 h-3.5" /> Password
            </button>
            <button onClick={onLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-emerald-400 text-[#0A192F] px-4 py-2 text-sm font-bold transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10" data-testid="admin-dashboard">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <button
            onClick={() => setTab("requests")}
            data-testid="admin-tab-requests"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === "requests" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            <Home className="w-4 h-4" /> Richieste case ({requests.length})
          </button>
          <button
            onClick={() => setTab("leads")}
            data-testid="admin-tab-leads"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === "leads" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            <Building2 className="w-4 h-4" /> Agenzie ({leads.length})
          </button>
          <button
            onClick={() => setTab("attivita")}
            data-testid="admin-tab-attivita"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === "attivita" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            <WalletIcon className="w-4 h-4" /> Pagamenti ({activity.sblocchi.length + activity.abbonamenti.length})
          </button>
          <button
            onClick={() => setTab("stats")}
            data-testid="admin-tab-stats"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === "stats" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            <BarChart3 className="w-4 h-4" /> Statistiche
          </button>
          <button
            onClick={() => setTab("assistenza")}
            data-testid="admin-tab-assistenza"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
              tab === "assistenza" ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            <LifeBuoy className="w-4 h-4" /> Assistenza ({supportChats.length})
          </button>
        </div>

        {tab === "assistenza" && (
          <div className="space-y-4" data-testid="admin-support-tab">
            {supportChats.length === 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500 text-sm">
                Nessun messaggio di assistenza ancora. I visitatori ti scrivono dal pulsante "Hai domande?" in basso a destra nel sito.
              </div>
            )}
            {supportChats.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`support-chat-${c.id}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-bold text-[#0A192F]">{c.nome}</p>
                    <p className="text-xs text-slate-500">{c.email} · {formatDate(c.created_at)}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${c.stato === "risposto" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {c.stato === "risposto" ? "Risposto" : "Da rispondere"}
                  </span>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 mb-4">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{c.messaggio}</p>
                </div>
                {c.risposta && (
                  <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 mb-4">
                    <p className="text-[11px] font-bold text-emerald-700 mb-1">La tua risposta</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{c.risposta}</p>
                  </div>
                )}
                <div className="flex gap-3">
                  <Textarea
                    value={replyDraft[c.id] || ""}
                    onChange={(e) => setReplyDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                    placeholder={c.risposta ? "Rispondi di nuovo..." : "Scrivi la tua risposta: il visitatore la riceve via email..."}
                    rows={2}
                    data-testid={`support-reply-${c.id}`}
                    className="flex-1 rounded-xl text-sm resize-none"
                  />
                  <button
                    type="button"
                    onClick={() => sendReply(c.id)}
                    disabled={sendingReply === c.id || !(replyDraft[c.id] || "").trim()}
                    data-testid={`support-send-${c.id}`}
                    className="self-end inline-flex items-center gap-2 rounded-full bg-[#10B981] text-white font-bold text-sm px-5 py-2.5 hover:bg-[#059669] transition-colors disabled:opacity-50"
                  >
                    {sendingReply === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Invia
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "requests" && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="admin-filters">            <div className="flex-1 min-w-[200px]">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">Cerca</Label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="admin-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nome, comune, WhatsApp, note..."
                  className="h-10 rounded-xl pl-10"
                />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">Provincia</Label>
              <Select value={filterProvincia} onValueChange={setFilterProvincia}>
                <SelectTrigger data-testid="admin-filter-provincia" className="h-10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="tutte">Tutte</SelectItem>
                  {provinceDisponibili.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-52">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">Tipo</Label>
              <Select value={filterTipo} onValueChange={setFilterTipo}>
                <SelectTrigger data-testid="admin-filter-tipo" className="h-10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti</SelectItem>
                  {TIPI_RICERCA.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">Stato</Label>
              <Select value={filterStato} onValueChange={setFilterStato}>
                <SelectTrigger data-testid="admin-filter-stato" className="h-10 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti</SelectItem>
                  {STATI.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">Dal</Label>
              <Input data-testid="admin-filter-dal" type="date" value={filterDal} onChange={(e) => setFilterDal(e.target.value)} className="h-10 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5 block">Al</Label>
              <Input data-testid="admin-filter-al" type="date" value={filterAl} onChange={(e) => setFilterAl(e.target.value)} className="h-10 rounded-xl" />
            </div>
            <button
              onClick={() =>
                tab === "requests"
                  ? downloadCsv("/admin/requests/export", "richieste-casamatch.csv")
                  : downloadCsv("/admin/agency-leads/export", "agenzie-casamatch.csv")
              }
              data-testid="admin-export-csv-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#10B981] transition-colors"
            >
              <Download className="w-4 h-4" /> Scarica CSV (Excel)
            </button>
          </div>
        )}

        {tab === "leads" && (
          <div className="flex justify-end mb-4">
            <button
              onClick={() => downloadCsv("/admin/agency-leads/export", "agenzie-casamatch.csv")}
              data-testid="admin-export-leads-csv-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-4 py-2.5 text-sm font-bold hover:bg-[#10B981] transition-colors"
            >
              <Download className="w-4 h-4" /> Scarica CSV (Excel)
            </button>
          </div>
        )}

        {tab === "stats" && <StatsTab />}

        {tab === "attivita" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-activity">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_10px_40px_rgba(10,25,47,0.05)] p-6">
              <h3 className="font-display font-bold text-[#0A192F] mb-4">Sblocchi singoli pagati ({activity.sblocchi.length})</h3>
              {activity.sblocchi.length === 0 ? (
                <p className="text-sm text-slate-400">Nessuno sblocco pagato finora.</p>
              ) : (
                <ul className="space-y-3">
                  {activity.sblocchi.map((u) => (
                    <li key={u.session_id} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
                      <div>
                        <p className="font-bold text-[#0A192F]">{u.agency_email}</p>
                        <p className="text-xs text-slate-400">richiesta {u.request_id.slice(0, 8)}… · {formatDate(u.created_at)}</p>
                      </div>
                      <span className="font-bold text-[#10B981]">€ {(u.amount / 100).toFixed(2).replace(".", ",")}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_10px_40px_rgba(10,25,47,0.05)] p-6">
              <h3 className="font-display font-bold text-[#0A192F] mb-4">Canoni annuali attivi ({activity.abbonamenti.length})</h3>
              {activity.abbonamenti.length === 0 ? (
                <p className="text-sm text-slate-400">Nessun abbonamento attivo finora.</p>
              ) : (
                <ul className="space-y-3">
                  {activity.abbonamenti.map((s) => (
                    <li key={s.session_id} className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm">
                      <div>
                        <p className="font-bold text-[#0A192F]">{s.agency_email}</p>
                        <p className="text-xs text-slate-400">attivo dal {formatDate(s.created_at)}</p>
                      </div>
                      <span className="font-bold text-[#10B981]">€ {(s.amount / 100).toFixed(2).replace(".", ",")}/anno</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {(tab === "attivita" || tab === "stats") ? null : loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-3xl border border-slate-200">
            <p className="font-display font-bold text-[#0A192F] text-lg">
              {tab === "requests" && requests.length > 0 ? "Nessun risultato con questi filtri" : "Nessun dato ancora"}
            </p>
            <p className="text-sm text-slate-500 mt-2">
              {tab === "requests" && requests.length > 0
                ? "Prova a modificare la ricerca o i filtri."
                : "Le nuove richieste appariranno qui automaticamente."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-[0_10px_40px_rgba(10,25,47,0.05)] overflow-x-auto">
            {tab === "requests" ? (
              <table className="w-full text-sm min-w-[1280px]" data-testid="admin-requests-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="px-4 py-4">Data</th>
                    <th className="px-4 py-4">Contatto</th>
                    <th className="px-4 py-4">Cerca</th>
                    <th className="px-4 py-4">Zona</th>
                    <th className="px-4 py-4">Dettagli</th>
                    <th className="px-4 py-4">Budget max</th>
                    <th className="px-4 py-4">Note richiedente</th>
                    <th className="px-4 py-4">Stato</th>
                    <th className="px-4 py-4">Note interne</th>
                    <th className="px-4 py-4 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors align-top">
                      <td className="px-4 py-4 whitespace-nowrap text-slate-500 text-xs">{formatDate(r.created_at)}</td>
                      <td className="px-4 py-4">
                        <p className="font-bold text-[#0A192F] whitespace-nowrap">{r.nome}</p>
                        <a href={`https://wa.me/${(r.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-[#10B981] font-semibold hover:underline text-xs whitespace-nowrap">
                          {r.whatsapp}
                        </a>
                        {r.email && <p className="text-xs text-slate-400 whitespace-nowrap">{r.email}</p>}
                      </td>
                      <td className="px-4 py-4 text-xs whitespace-nowrap font-bold text-[#0A192F]">{cercoLabel(r.tipo_ricerca)}</td>
                      <td className="px-4 py-4 text-xs">
                        <p className="whitespace-nowrap">{r.comune} ({r.provincia})</p>
                        {r.raggio_km && <p className="text-slate-400">+{r.raggio_km} km</p>}
                      </td>
                      <td className="px-4 py-4 text-xs text-slate-600 max-w-[200px]">
                        {[
                          ...(r.tipi_immobile || []),
                          r.camere_min && `${r.camere_min}+ camere`,
                          r.bagni_min && `${r.bagni_min}+ bagni`,
                          r.mq,
                          r.periodo && `📅 ${r.periodo}`,
                          ...(r.zone || []),
                          ...(r.caratteristiche || []),
                        ].join(" · ") || "—"}
                        {r.pagamento_acquisto && (
                          <p className="mt-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1 inline-block">
                            {r.pagamento_acquisto}{r.consulenza_broker ? " · broker ✓" : ""}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap font-semibold">€ {Number(r.budget_max).toLocaleString("it-IT")}</td>
                      <td className="px-4 py-4 text-xs text-slate-500 max-w-[180px]">{r.note || "—"}</td>
                      <td className="px-4 py-4">
                        <Select value={r.status || "nuova"} onValueChange={(v) => updateStatus(r.id, v)}>
                          <SelectTrigger
                            data-testid={`admin-status-${r.id}`}
                            className={`h-8 rounded-full text-[11px] font-bold border-0 w-[130px] ${statoInfo(r.status || "nuova").cls}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATI.map((s) => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-4 min-w-[200px]">
                        <Textarea
                          data-testid={`admin-note-${r.id}`}
                          value={editingNote[r.id] !== undefined ? editingNote[r.id] : (r.note_interne || "")}
                          onChange={(e) => setEditingNote((n) => ({ ...n, [r.id]: e.target.value }))}
                          placeholder="Appunti privati..."
                          className="text-xs rounded-lg min-h-[56px] bg-slate-50"
                        />
                        {editingNote[r.id] !== undefined && (
                          <button
                            onClick={() => saveNote(r.id)}
                            disabled={savingNote === r.id}
                            data-testid={`admin-note-save-${r.id}`}
                            className="mt-1.5 text-[11px] font-bold text-[#10B981] hover:underline disabled:opacity-50"
                          >
                            {savingNote === r.id ? "Salvataggio..." : "Salva nota"}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => deleteRequest(r.id)}
                          data-testid={`admin-delete-${r.id}`}
                          className="text-slate-300 hover:text-red-500 transition-colors"
                          title="Elimina richiesta"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm min-w-[800px]" data-testid="admin-leads-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                    <th className="px-5 py-4">Data</th>
                    <th className="px-5 py-4">Agenzia</th>
                    <th className="px-5 py-4">Zona</th>
                    <th className="px-5 py-4">Email</th>
                    <th className="px-5 py-4">Telefono</th>
                    <th className="px-5 py-4">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50/70 transition-colors">
                      <td className="px-5 py-4 whitespace-nowrap text-slate-500">{formatDate(l.created_at)}</td>
                      <td className="px-5 py-4 font-bold text-[#0A192F] whitespace-nowrap">{l.nome_agenzia}</td>
                      <td className="px-5 py-4 whitespace-nowrap">{l.zona}</td>
                      <td className="px-5 py-4 whitespace-nowrap text-[#10B981] font-semibold">{l.email}</td>
                      <td className="px-5 py-4 whitespace-nowrap">{l.telefono}</td>
                      <td className="px-5 py-4 text-slate-500 text-xs max-w-[220px]">{l.note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />}
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { headers: authHeaders(), withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem("cm_token");
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#10B981]" />
      </div>
    );
  }
  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginForm onLogin={setUser} />;
}
